import AppDate from '@/classes/AppDate';
import InternetPriceSavings from '@/interfaces/InternetPriceSavings';
import ProductContractDetailsInterface from '@/interfaces/ProductContractDetailsInterface';
import ProductInterface from '@/interfaces/ProductInterface';
import ProductPriceInterface from '@/interfaces/ProductPriceInterface';
import ProductPriceInternetInterface from '@/interfaces/ProductPriceInternetInterface';
import ProductPriceMappedDataInterface from '@/interfaces/ProductPriceMappedDataInterface';
import BonusInterface from '@/interfaces/BonusInterface';
import { formatNumber, sumObjectValues } from '@/helpers';
import ProductCategoryInterface from '@/interfaces/ProductCategoryInterface';
import ProductImage from '@/interfaces/ProductImageInterface';
import ProductDocumentInterface from '@/interfaces/ProductDocumentInterface';

export interface ProductBonus {
  type: string,
  kind: string,
  value: number,
  valueGross: number,
  valueFormatted: string,
  valueGrossFormatted: string,
  label: string,
  description?: string
}

export default class Product {
  attributes: ProductInterface;

  priceKeys: string[];

  priceFormats: Record<'multiply' | 'ceil', Record<string, number>>;

  translations: Record<'branch', Record<string, string>>;

  useEco: boolean;

  constructor(attributes: ProductInterface) {
    this.attributes = attributes;

    if (this.attributes.price) {
      this.useEco = `${this.attributes.price.params.bio}` === '1';
    } else if (this.attributes.priceEco) {
      this.useEco = `${this.attributes.priceEco.params.bio}` === '1';
    } else {
      this.useEco = false;
    }

    this.priceKeys = [
      'workingPrice',
      'workingPriceHt',
      'workingPriceNt',
      'basePrice',
      'yearly',
      'monhtly',
      'savingsYearly',
      'savingsMonthly',
    ];

    this.priceFormats = {
      multiply: {
        workingPrice: 100,
        workingPriceHt: 100,
        workingPriceNt: 100,
      },
      ceil: {
        monthly: 1,
      },
    };

    this.translations = {
      branch: {
        electricity: 'Strom',
        power: 'Strom',
        gas: 'Gas',
        heat: 'Heizstrom',
      },
    };
  }

  get addons(): Array<{ category: string, item: any }> {
    return this.attributes.addons || [];
  }

  set addons(addons: Array<{ category: string, item: any }>) {
    this.attributes.addons = addons;
  }

  get productType(): string {
    return this.attributes.category.isCommodity ? 'commodity' : this.category.key;
  }

  get price(): ProductPriceInterface | undefined {
    if (this.useEco) {
      return this.attributes.priceEco;
    }

    return this.attributes.price;
  }

  set price(price: ProductPriceInterface | undefined) {
    this.attributes.price = price;
    if (price) {
      this.useEco = `${price.params.bio}` === '1';
    }
  }

  set priceEco(priceEco: ProductPriceInterface | undefined) {
    this.attributes.priceEco = priceEco;
    if (priceEco) {
      this.useEco = `${priceEco.params.bio}` === '1';
    }
  }

  get isInternet(): boolean {
    return this.category.key === 'internet';
  }

  get hasEco(): boolean {
    return this.attributes.price !== undefined
      && this.attributes.priceEco !== undefined;
  }

  get ecoDifference(): number {
    if (this.hasEco && !this.isInternet) {
      const priceEco = this.attributes.priceEco?.mapped.active as ProductPriceMappedDataInterface | undefined;
      const price = this.attributes.price?.mapped.active as ProductPriceMappedDataInterface | undefined;

      return (priceEco?.monthlyWithBonus || 2) - (price?.monthlyWithBonus || 1);
    }

    return 0;
  }

  get ecoDifferenceGross(): number {
    if (this.hasEco && !this.isInternet) {
      const priceEco = this.attributes.priceEco?.mapped.active as ProductPriceMappedDataInterface | undefined;
      const price = this.attributes.price?.mapped.active as ProductPriceMappedDataInterface | undefined;

      return (priceEco?.monthlyWithBonusGross || 2) - (price?.monthlyWithBonusGross || 1);
    }

    return 0;
  }

  get priceCustomerType(): string {
    return this.price?.params?.customerType || 'private';
  }

  get priceIsDouble(): boolean {
    return !!this.attributes?.price?.params?.double;
  }

  get appliedBonuses(): BonusInterface[] {
    if (!this.isInternet) {
      const price = this.attributes.price?.mapped.active as ProductPriceMappedDataInterface | undefined;

      return price?.appliedBonus || [];
    }

    return [];
  }

  get name(): string {
    let formattedProductName = this.attributes.name;

    formattedProductName = formattedProductName.replace('Badlantic ', '<b>Badlantic</b>&nbsp;');

    return formattedProductName;
  }

  get id(): number {
    return this.attributes.id;
  }

  get benefits(): string[] {
    return this.attributes.benefits || [];
  }

  get branchTranslated(): string {
    return this.translations.branch[this.branch];
  }

  get isRecommended(): boolean {
    return this.attributes.recommended || false;
  }

  get category(): ProductCategoryInterface {
    return this.attributes.category;
  }

  get branch(): string {
    return this.category.key;
  }

  public getRecommendationText(isSingle = false): string | undefined {
    if (this.isRecommended) {
      return isSingle ? (
        this.attributes.recommendationTextSingle
        || this.attributes.recommendationText
        || undefined
      ) : (
        this.attributes.recommendationText
        || undefined
      );
    }

    return undefined;
  }

  get slogan(): string | null {
    return this.attributes.slogan || null;
  }

  get priceFileUrl(): string | null {
    return this.price?.pricefile || null;
  }

  get logoUrl(): string | null {
    let sourceUrl = this.attributes.thumbnail;
    const images = Object.values(this.attributes.details?.media?.images || {});

    if (sourceUrl === null && images.length) {
      let logoSource = images.find((m: ProductImage) => m.key === 'logo');

      if (logoSource) {
        sourceUrl = `${logoSource.url || ''}`.trim();
      }

      if (sourceUrl === null || sourceUrl.length === 0) {
        logoSource = images.find((m: ProductImage) => m.isPreview);

        if (logoSource) {
          sourceUrl = `${logoSource.url || ''}`.trim();
        }
      }
    }

    return sourceUrl || null;
  }

  public getDocumentByKey(key: string): ProductDocumentInterface | null {
    return Object.values(this.attributes.details.media?.documents ?? {}).find((m: ProductDocumentInterface) => m.key === key) || null;
  }

  public getDocumentUrlByKey(key: string): string {
    return this.getDocumentByKey(key)?.url || '#';
  }

  get priceUsageFormatted(): string {
    return formatNumber(this.price?.params.usage || 0, 0);
  }

  get hasPriceGuarantee(): boolean {
    return this.contractDetails.hasPriceGuarantee || false;
  }

  get contractDetails(): ProductContractDetailsInterface {
    return this.attributes.details.contractDetails || this.attributes.contractDetails;
  }

  private getContractDetail<AwaitedType>(key: string): AwaitedType | undefined {
    const snakeCaseKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

    const value = this.contractDetails[key as keyof ProductContractDetailsInterface];

    return (
      value !== undefined
        ? value
        : (this.contractDetails[snakeCaseKey as keyof ProductContractDetailsInterface] || undefined)
    ) as AwaitedType | undefined;
  }

  get contractTermIsFixed(): boolean {
    return !this.getContractDetail<number | null>('contractTermMonths') && !!this.getContractDetail<string>('contractTermFixed');
  }

  get contractTermLabel(): string {
    if (this.contractTermIsFixed) {
      const contractTermDateString = this.getContractDetail<string>('contractTermFixed') || '';
      const contractTermDate = new AppDate(contractTermDateString);

      return `${`${contractTermDate}` !== 'Invalid Date' ? contractTermDate.format('d.m.Y') : contractTermDateString}`;
    }

    const contractTermMonths = parseInt(`${this.getContractDetail<number | null>('contractTermMonths') || 0}`, 10);

    return `${contractTermMonths} Monat${contractTermMonths > 1 ? 'e' : ''}`;
  }

  public setEcoParam(eco: boolean): void {
    this.useEco = eco;
    if (this.attributes.price) {
      this.attributes.price.params.bio = eco ? 1 : 0;
    }
    if (this.attributes.priceEco) {
      this.attributes.priceEco.params.bio = eco ? 1 : 0;
    }
  }

  get priceGuaranteeIsFixed(): boolean {
    return this.contractDetails.priceGuaranteeTermMonths === null && !!this.contractDetails.priceGuaranteeTermFixed;
  }

  get priceGuaranteeLabel(): string {
    if (
      this.priceGuaranteeIsFixed
      && this.contractDetails.priceGuaranteeTermFixed
    ) {
      const priceGuaranteeDateString = this.contractDetails.priceGuaranteeTermFixed;
      const priceGuaranteeDate = new AppDate(priceGuaranteeDateString);

      return `${`${priceGuaranteeDate}` !== 'Invalid Date' ? priceGuaranteeDate.format('d.m.Y') : priceGuaranteeDateString}`;
    }

    return this.contractDetails.priceGuaranteeTermMonths ? `${this.contractDetails.priceGuaranteeTermMonths} Monate` : 'Keine';
  }

  get noticePeriodLabel(): string {
    if (this.contractDetails.noticePeriodValueDate) {
      const noticePeriodDateString = this.contractDetails.noticePeriodValueDate;
      const noticePeriodDate = new AppDate(noticePeriodDateString);

      return `${`${noticePeriodDate}` !== 'Invalid Date' ? noticePeriodDate.format('d.m.Y') : noticePeriodDateString}`;
    }

    const periodUnit = this.contractDetails.noticePeriodUnit;
    const period = parseInt(this.contractDetails.noticePeriodValue || '0', 10);

    let periodSuffix = `Monat${period > 1 ? 'e' : ''}`;

    if (periodUnit === 'week') {
      periodSuffix = `Woche${period > 1 ? 'n' : ''}`;
    } else if (periodUnit === 'quarter') {
      periodSuffix = `Quartal${period > 1 ? 'e' : ''}`;
    }

    return period ? `${period} ${periodSuffix}` : 'Keine';
  }

  private applyPriceFormats(key: string, value: number, withBonus = false) {
    let formatKey = key;

    if (withBonus) {
      formatKey = `${formatKey}WithBonus`;
    }

    const valueFormatted = value * (this.priceFormats.multiply[formatKey] || this.priceFormats.multiply[key] || 1);
    const ceilingValue = this.priceFormats.ceil[formatKey] || valueFormatted;

    return Math.ceil(valueFormatted / ceilingValue) * ceilingValue;
  }

  public getPriceFormatted(key: string, digits = 2, priceType: 'gross' | 'net' | null = null): string {
    return formatNumber(this.applyPriceFormats(key, this.getPriceValue(key, priceType), true), digits);
  }

  public getAbsPriceFormatted(key: string, digits = 2, priceType: 'gross' | 'net' | null = null): string {
    return formatNumber(this.applyPriceFormats(key, Math.abs(this.getPriceValue(key, priceType)), true), digits);
  }

  public getPriceWithoutBonusFormatted(key: string, digits = 2, priceType: 'gross' | 'net' | null = null): string {
    return formatNumber(this.applyPriceFormats(key, this.getPriceValueWithoutBonus(key, priceType), false), digits);
  }

  public getAbsPriceWithoutBonusFormatted(key: string, digits = 2, priceType: 'gross' | 'net' | null = null): string {
    return formatNumber(this.applyPriceFormats(key, Math.abs(this.getPriceValueWithoutBonus(key, priceType)), false), digits);
  }

  public getRecurringPriceValue(type: string, withAddons = false): number {
    if (!this.isInternet) return 0;

    const price = this.price?.mapped.active as ProductPriceInternetInterface | undefined;

    let value = price?.recurring.find((recurringPrice) => recurringPrice.type === type)?.value || 0;

    if (withAddons && this.addons.length) {
      this.addons.forEach((addon) => {
        const addonValue = addon?.item?.price?.recurring?.find((recurringPrice: any) => recurringPrice?.type === type)?.value || 0;

        value += addonValue;
      });
    }

    return value;
  }

  public getRecurringPriceValueFormatted(type: string, digits = 2, withAddons = false): string {
    return formatNumber(this.getRecurringPriceValue(type, withAddons), digits);
  }

  public getRecurringPriceWithSavings(type: string, withAddons = false): number {
    const savingValue = this.getPriceSavings()[0]?.value || 0;

    return this.getRecurringPriceValue(type, withAddons) - (type === 'base_price' ? savingValue : 0);
  }

  public getRecurringPriceWithSavingsFormatted(type: string, digits = 2, withAddons = false): string {
    return formatNumber(this.getRecurringPriceWithSavings(type, withAddons), digits);
  }

  public getOneTimePriceValue(type: string | string[], withAddons = false): number {
    if (!this.isInternet) return 0;

    const price = this.price?.mapped.active as ProductPriceInternetInterface | undefined;

    let value = price?.oneTime.find((oneTimePrice) => (
      Array.isArray(type) ? type.includes(oneTimePrice.type) : oneTimePrice.type === type
    ))?.value || 0;

    if (withAddons && this.addons.length) {
      this.addons.forEach((addon) => {
        const addonValue = addon?.item?.price?.oneTime?.find((oneTimePrice: any) => (
          Array.isArray(type) ? type.includes(oneTimePrice.type) : oneTimePrice.type === type
        ))?.value || 0;

        value += addonValue;
      });
    }

    return value;
  }

  public getOneTimePriceValueFormatted(type: string | string[], digits = 2, withAddons = false): string {
    return formatNumber(this.getOneTimePriceValue(type, withAddons), digits);
  }

  public getPriceSavings(): InternetPriceSavings[] {
    if (!this.isInternet) return [];

    const price = this.price?.mapped.active as ProductPriceInternetInterface | undefined;

    return (price?.savings || []).sort((savingA, savingB) => savingA.fromMonth - savingB.fromMonth);
  }

  public getPriceValue(key: string, priceType: 'gross' | 'net' | null = null): number {
    if (this.isInternet) return 0;

    let value = 0;
    const price = this.price?.mapped.active as ProductPriceMappedDataInterface;

    if (key === 'eco') {
      return this.priceCustomerType === 'private' || priceType === 'gross' ? this.ecoDifferenceGross : this.ecoDifference;
    }

    if (this.priceCustomerType === 'private' || priceType === 'gross') {
      const valGross = price?.[`${key}WithBonusGross` as keyof ProductPriceMappedDataInterface]
        || this.getPriceValueWithoutBonus(key);
      if (typeof valGross === 'number') {
        value = valGross;
      }
    } else if (this.priceCustomerType === 'business' || priceType === 'net') {
      const valNet = price?.[`${key}WithBonus` as keyof ProductPriceMappedDataInterface]
        || this.getPriceValueWithoutBonus(key);

      if (typeof valNet === 'number') {
        value = valNet;
      }
    }

    return value;
  }

  public getPriceValueWithoutBonus(key: string, priceType: 'gross' | 'net' | null = null): number {
    if (this.isInternet) return 0;

    let value = 0;
    const price = this.price?.mapped.active as ProductPriceMappedDataInterface;

    if (this.priceCustomerType === 'private' || priceType === 'gross') {
      const valGross = price?.[`${key}Gross` as keyof ProductPriceMappedDataInterface] || 0;
      if (typeof valGross === 'number') {
        value = valGross;
      }
    } else if (this.priceCustomerType === 'business' || priceType === 'net') {
      const valNet = price?.[`${key}` as keyof ProductPriceMappedDataInterface] || 0;

      if (typeof valNet === 'number') {
        value = valNet;
      }
    }

    return value;
  }

  get bonuses(): ProductBonus[] {
    if (!this.appliedBonuses.length) return [];

    const bonuses: ProductBonus[] = [];

    this.appliedBonuses.forEach((bonus) => {
      if (bonus.bonus_value === 0) return;

      const mappedBonus: ProductBonus = {
        type: bonus.bonus_type,
        kind: bonus.kind,
        value: 0,
        valueGross: 0,
        valueFormatted: '0',
        valueGrossFormatted: '0',
        label: '',
        description: undefined,
      };

      switch (bonus.kind) {
        case 'other':
          mappedBonus.label = 'Bonus';
          break;
        default:
          mappedBonus.label = bonus.name;
      }

      mappedBonus.value = bonus.bonus_value;
      mappedBonus.valueGross = bonus.bonus_valueGross;
      mappedBonus.valueFormatted = formatNumber(bonus.bonus_value, 2);
      mappedBonus.valueGrossFormatted = formatNumber(bonus.bonus_valueGross, 2);

      let description: string | undefined = `${bonus.description || ''}`.replace(/(<([^>]+)>)/gi, '').trim();
      if (description === '') {
        description = undefined;
      }
      mappedBonus.description = description;

      bonuses.push(mappedBonus);
    });

    return bonuses;
  }

  public getBonusesByType(type: string): ProductBonus[] {
    return this.bonuses.filter((bonus) => bonus.type === type);
  }

  public getSummedBonus(type?: string): ProductBonus | undefined {
    const bonuses = type ? this.bonuses : this.getBonusesByType(type as string);
    if (!bonuses.length) return undefined;
    if (bonuses.length === 1) return bonuses[0];

    const sumValue: number = sumObjectValues<number>(bonuses, 'value', 0);
    const sumValueGross: number = sumObjectValues<number>(bonuses, 'valueGross', 0);

    return {
      type: type || 'any',
      kind: 'any',
      label: 'Bonus',
      value: sumValue,
      valueGross: sumValueGross,
      valueFormatted: formatNumber(sumValue, 2),
      valueGrossFormatted: formatNumber(sumValueGross, 2),
    };
  }
}
